import type { CSSProperties, ReactNode } from "react";
import styles from "./WindowMock.module.css";

export interface WindowMockProps {
  title: string;
  /** Optional small icon shown left of the title (raw SVG string). */
  iconSvg?: string;
  /** Optional inline style overrides — used for tilt/translate accents. */
  style?: CSSProperties;
  /** Optional extra class — useful for sizing/positioning per-instance. */
  className?: string;
  /** Body padding flavour. "tight" for note-like content, "flush" when
   *  the body owns its own padding (eg. image content). */
  bodyPadding?: "tight" | "flush";
  children: ReactNode;
}

/**
 * Static visual mock of an Atlas window: mustard titlebar with the three
 * traffic-light buttons + title, ink-bordered paper body, hard offset
 * shadow. Used throughout the marketing site as a content container so
 * the page literally feels like little pieces of the suite floating in.
 *
 * Intentionally not interactive — we're not building a fake OS, we're
 * showing what the real one looks like. Hover lifts each window slightly
 * to acknowledge the user but doesn't pretend to be draggable.
 */
export function WindowMock({
  title,
  iconSvg,
  style,
  className,
  bodyPadding = "tight",
  children,
}: WindowMockProps) {
  return (
    <article
      className={[styles.window, className].filter(Boolean).join(" ")}
      style={style}
      aria-label={title}
    >
      <header className={styles.titlebar}>
        <div className={styles.titlebarLeft}>
          {iconSvg ? (
            <span
              className={styles.titlebarIcon}
              dangerouslySetInnerHTML={{ __html: iconSvg }}
            />
          ) : null}
          <span className={styles.titlebarTitle}>{title}</span>
        </div>
        <div className={styles.lights} aria-hidden>
          <span className={styles.light} />
          <span className={styles.light} />
          <span className={styles.lightClose} />
        </div>
      </header>
      <div
        className={
          bodyPadding === "tight" ? styles.bodyTight : styles.bodyFlush
        }
      >
        {children}
      </div>
    </article>
  );
}
